import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import {
  TextField,
  InputAdornment,
  IconButton,
  Menu,
  MenuItem,
  ListItemText,
  Box,
} from "@mui/material";
import Flags from "country-flag-icons/react/3x2";
import { AsYouType } from "libphonenumber-js";
import { allCountries } from "material-ui-phone-number-2/src/country_data";

const COUNTRIES = allCountries
  .filter((c) => !c.isAreaCode)
  .map((c) => ({
    code: c.iso2,
    name: c.name,
    dialCode: c.dialCode,
  }));

const DEFAULT_COUNTRY = "nl";
const MAX_DIGITS = 20;

const FlagIcon = ({ countryCode, size = 20 }) => {
  const code = countryCode.toUpperCase();
  const Component = Flags[code];
  if (!Component) return null;
  return <Component style={{ width: size, height: Math.round(size * 0.67) }} />;
};

const detectCountryFromValue = (value) => {
  if (!value?.startsWith("+")) return null;
  const digits = value.slice(1);

  // Try longest dial codes first (4, 3, 2, 1 digits)
  for (let len = 4; len >= 1; len--) {
    if (digits.length < len) continue;
    const prefix = digits.slice(0, len);
    const match = COUNTRIES.find((c) => c.dialCode === prefix);
    if (match) return match.code;
  }
  return null;
};

const getStoredCountry = () => {
  const stored = localStorage.getItem("phoneCountry");
  return stored && stored !== "null" ? stored.toLowerCase() : DEFAULT_COUNTRY;
};

export default function PhoneInput({
  value,
  onChange,
  onBlur,
  label,
  error,
  helperText,
  disabled,
}) {
  const [selectedCountry, setSelectedCountry] = useState(() => {
    const detected = detectCountryFromValue(value);
    return detected || getStoredCountry();
  });
  const [anchorEl, setAnchorEl] = useState(null);
  const inputRef = useRef(null);

  const country = useMemo(
    () => COUNTRIES.find((c) => c.code === selectedCountry) || COUNTRIES[0],
    [selectedCountry],
  );

  const hasPlus = value?.startsWith("+");

  // Empty value shows the dial-code prefix (display-only; the stored value stays
  // "" until a digit is typed, so an untouched field never persists "+31").
  const displayValue = useMemo(() => {
    if (!value) return `+${country.dialCode}`;
    if (!hasPlus) return value;
    return new AsYouType().input(value);
  }, [value, hasPlus, country]);

  // Sync selected country when value changes and starts with +
  useEffect(() => {
    if (hasPlus && value.length > 1) {
      const detected = detectCountryFromValue(value);
      if (detected && detected !== selectedCountry) {
        setSelectedCountry(detected);
      }
    }
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCountrySelect = useCallback(
    (countryCode) => {
      const newCountry = COUNTRIES.find((c) => c.code === countryCode);
      if (!newCountry) return;

      setSelectedCountry(countryCode);
      localStorage.setItem("phoneCountry", countryCode);
      setAnchorEl(null);

      // Replace dial code prefix in value
      const oldDialCode = country.dialCode;
      const currentValue = value || "";
      let suffix = "";

      if (currentValue.startsWith(`+${oldDialCode}`)) {
        suffix = currentValue.slice(1 + oldDialCode.length);
      } else if (currentValue.startsWith("+")) {
        // Value starts with + but doesn't match old dial code then keep digits after +
        suffix = currentValue.slice(1).replace(/^\d+/, "");
      }

      // No national number yet — switch the displayed prefix only; keep the
      // stored value empty so changing the flag alone never persists a dial code.
      if (!/\d/.test(suffix)) {
        onChange("");
        return;
      }

      const newValue = `+${newCountry.dialCode}${suffix}`;
      const digitCount = newValue.replace(/\D/g, "").length;
      onChange(digitCount <= MAX_DIGITS ? newValue : `+${newCountry.dialCode}`);
    },
    [country, value, onChange],
  );

  const handleChange = useCallback(
    (e) => {
      // Strip formatting to get the raw value
      let newValue = e.target.value.replace(/[\s\-()]/g, "");

      // Only allow + and digits
      if (newValue && !/^\+?\d*$/.test(newValue)) return;

      // A bare national number gets the dial code; but a bare dial code (user
      // deleted the "+") is the prefix itself — don't prepend again (would double).
      if (newValue && !newValue.startsWith("+")) {
        newValue =
          newValue === country.dialCode
            ? `+${country.dialCode}`
            : `+${country.dialCode}${newValue.replace(/^0+/, "")}`;
      }

      if (newValue.replace(/\D/g, "").length > MAX_DIGITS) return;

      onChange(newValue);
    },
    [country, onChange],
  );

  return (
    <>
      <TextField
        inputRef={inputRef}
        value={displayValue}
        onChange={handleChange}
        onBlur={onBlur}
        label={label}
        error={error}
        helperText={helperText}
        disabled={disabled}
        InputProps={{
          // Always show the flag so it can't vanish when the value is empty.
          startAdornment: (
            <InputAdornment position="start">
              <IconButton
                size="small"
                disabled={disabled}
                onClick={(e) => setAnchorEl(e.currentTarget)}
                sx={{ p: 0.5 }}
              >
                <FlagIcon countryCode={selectedCountry} size={24} />
              </IconButton>
            </InputAdornment>
          ),
        }}
        sx={{
          width: "100%",
          "& .MuiFormHelperText-root": { marginLeft: 0 },
        }}
      />
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        slotProps={{ paper: { sx: { maxHeight: 300 } } }}
      >
        {COUNTRIES.map((c) => (
          <MenuItem
            key={c.code}
            selected={c.code === selectedCountry}
            onClick={() => handleCountrySelect(c.code)}
          >
            <Box sx={{ mr: 1, display: "flex", alignItems: "center" }}>
              <FlagIcon countryCode={c.code} size={20} />
            </Box>
            <ListItemText>
              {c.name} (+{c.dialCode})
            </ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
